// Racine de l'application : cache de requêtes, session, préférences, routes.
import { FournisseurLien, type ProprietesLien } from '@eec/ui';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { lazy, Suspense } from 'react';
import { createBrowserRouter, Link, Navigate, RouterProvider } from 'react-router';
import { ErreurApi } from './api/erreurs';
import { FournisseurMessages } from './commun/Messages';
import { CoquilleAdministration } from './coquille/CoquilleAdministration';
import { GardeContexte, GardeSession } from './Gardes';
import { PageActivationMfa } from './pages/acces/PageActivationMfa';
import { PageChoixContexte } from './pages/acces/PageChoixContexte';
import { PageConnexion } from './pages/acces/PageConnexion';
import { PageIntrouvable } from './pages/PageAttente';
import { PagePreferences } from './pages/PagePreferences';
import { routesNoyau } from './pages/administration/routes-noyau';
import { PageAccueil } from './pages/PageAccueil';
import { PageIncidentsReseau, PageIncidentReseau } from './pages/incidents/PageIncidentsReseau';
import { PageSignalementsReseau } from './pages/incidents/PageSignalementsReseau';
import { PageInterventionsReseau } from './pages/incidents/PageInterventionsReseau';
import { PageStocks } from './pages/incidents/PageStocks';
import { PagePatrimoine } from './pages/incidents/PagePatrimoine';
import { PageMaintenance } from './pages/incidents/PageMaintenance';
import { PageEquipes } from './pages/incidents/PageEquipes';
import { PageNotifications } from './pages/incidents/PageNotifications';
import { PagePortailPublic } from './pages/incidents/PagePortailPublic';
import { PagePilotageReseaux } from './pages/incidents/PagePilotageReseaux';
import { PageCouts } from './pages/incidents/PageCouts';
import { PageInvestissements } from './pages/incidents/PageInvestissements';
import { PageInstructionInvestissements } from './pages/incidents/PageInstructionInvestissements';
import { PageRegulation } from './pages/incidents/PageRegulation';
import { PageMandats } from './pages/administration/PageMandats';
import { PageCommunication } from './pages/incidents/PageCommunication';
import { PageTerrainReseau } from './terrain-reseau/PageTerrainReseau';
import { PageReclamationsReseau } from './pages/incidents/PageReclamationsReseau';
import { FournisseurPreferences } from './preferences/FournisseurPreferences';
import { FournisseurSession } from './session/FournisseurSession';

// Vitrine des composants : chargée uniquement en développement, absente de la construction.
const PageVitrine = import.meta.env.DEV ? lazy(() => import('./vitrine/PageVitrine')) : null;

export function creerClientRequetes(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        // Pas de nouvel essai sur une erreur fonctionnelle (4xx) ; deux sur une coupure réseau.
        retry: (n, e) => (e instanceof ErreurApi && e.statut >= 400 && e.statut < 500 ? false : n < 2),
        refetchOnWindowFocus: false,
        staleTime: 15_000,
        networkMode: 'online',
      },
      mutations: { retry: false },
    },
  });
}

function LienRouteur({ href, ...reste }: ProprietesLien) {
  return <Link to={href} {...reste} />;
}

const routeur = createBrowserRouter([
  { path:'/terrain',element:<PageTerrainReseau/> },
  { path:'/citoyen',element:<PagePortailPublic/> },
  { path: '/connexion', element: <PageConnexion /> },
  { path: '/supervision/*', element: <Navigate to="/pilotage-reseaux" replace /> },
  ...(PageVitrine
    ? [
        {
          path: '/vitrine',
          element: (
            <Suspense fallback={null}>
              <PageVitrine />
            </Suspense>
          ),
        },
      ]
    : []),
  {
    element: <GardeSession />,
    children: [
      { path: '/contexte', element: <PageChoixContexte /> },
      { path: '/mfa/activer', element: <PageActivationMfa /> },
      {
        element: <GardeContexte />,
        children: [
          {
            element: <CoquilleAdministration />,
            children: [
              { index: true, element: <PageAccueil /> },
              { path: '/incidents', element: <PageIncidentsReseau /> },
              { path: '/reclamations-reseau', element: <PageReclamationsReseau /> },
              { path: '/signalements', element: <PageSignalementsReseau /> },
              { path: '/interventions-reseau', element: <PageInterventionsReseau /> },
              { path: '/stocks', element: <PageStocks /> },
              { path: '/patrimoine', element: <PagePatrimoine /> },
              { path: '/maintenance', element: <PageMaintenance /> },
              { path: '/equipes-reseau', element: <PageEquipes /> },
              { path: '/notifications-reseau', element: <PageNotifications /> },
              { path: '/pilotage-reseaux', element: <PagePilotageReseaux /> },
              { path: '/couts-reseau', element: <PageCouts /> },
              { path: '/investissements-reseau', element: <PageInvestissements /> },
              { path: '/instruction-investissements', element: <PageInstructionInvestissements /> },
              { path: '/controles-regulation', element: <PageRegulation /> },
              { path: '/mandats-institutionnels', element: <PageMandats /> },
              { path: '/communication-reseau', element: <PageCommunication /> },
              { path: '/incidents/:id', element: <PageIncidentReseau /> },
              ...routesNoyau,
              { path: '/plateforme/modules', element: <Navigate to="/" replace /> },
              { path: '/preferences', element: <PagePreferences /> },
              { path: '/administration', element: <Navigate to="/administration/utilisateurs" replace /> },
              { path: '*', element: <PageIntrouvable /> },
            ],
          },
        ],
      },
    ],
  },
]);

const client = creerClientRequetes();

export function Application() {
  return (
    <QueryClientProvider client={client}>
      <FournisseurLien composant={LienRouteur}>
        <FournisseurMessages>
          <FournisseurSession>
            <FournisseurPreferences>
              <RouterProvider router={routeur} />
            </FournisseurPreferences>
          </FournisseurSession>
        </FournisseurMessages>
      </FournisseurLien>
    </QueryClientProvider>
  );
}
